import React, { useEffect, useCallback, useMemo, useState, useRef, lazy, Suspense, forwardRef, useImperativeHandle } from "react";
import CommonAPI from "services/CommonAPI";
import { Col, Row, Button, Input, InputGroup, SelectPicker, ButtonToolbar, Panel, Text, DatePicker, AutoComplete } from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import helper from "lib/helper";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "components/Loader/Loader";
import ShowFormError from "components/common/showFormError/ShowFormError";
import { Loader as LoaderInline } from "rsuite";

const ConformationModal = lazy(() => import("./ConformationModal"));

export default forwardRef(function Filter(
  {
    pageName,
    searchWidth,
    buttonLabel,
    onConfirm,
    pageType,
    showConformation = false,
    preserveQueries = [],
    customFilter = null,
    onFilterUpdate,
    shouldDisableDate = {
      start_date: () => {},
      end_date: () => {},
    },
    isCleanable = true,
    onSearchChange = () => {},
    searchBtnLabel = "Search",
    showInlineLoader = false,
    disabled=false
  },
  filterRef,
) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const filterObj = useRef({});
  let [requiredEle, setRequiredEle] = useState([]);
  let prevChangeItem = useRef({ name: "", selectedValue: "" });
  const [autoSearchData, setAutoSearchData] = useState([]);
  const [searchLoader, setSearchLoader] = useState(false);

  const apiInstance = useMemo(() => new CommonAPI(), []);
  useImperativeHandle(filterRef, () => ({
    filters: filterObj.current,
    clear: clearSelectedValues,
    data,
    setData,
  }));

  useEffect(() => {
    setRequiredEle([]);
  }, [location.pathname]);

  useEffect(() => {
    const updatedParams = helper.fetchUpdatedQuery();
    filterObj.current = updatedParams;
    const fetchAPI = async () => {
      try {
        const res = !customFilter ? await apiInstance.filterAPI(pageName, pageType) : customFilter;
        const updatedData = res?.data?.map((item, index) => ({
          ...item,
          id: index + 1,
          selectedValue: "",
        }));

        if (Object.keys(updatedParams).length > 0) {
          const updatedParamsData = updatedData?.map((item) => ({
            ...item,
            selectedValue: updatedParams[item.name] || item.selectedValue,
          }));
          setData(updatedParamsData);
        } else {
          setData(updatedData);
        }
      } catch (error) {
        console.log("Error fetching data:", error);
      }
    };
    fetchAPI();
  }, [apiInstance, pageName, pageType, customFilter]);

  const handleChange = (value, id) => {
    const updatedData = data?.map((item) => {
      if (item.id === id) {
        prevChangeItem.current = { name: item.name, selectedValue: value };
      }
      return item.id === id ? { ...item, selectedValue: value } : item;
    });
    setData(updatedData);
  };

  const handleClean = (id) => {
    const updatedData = data?.map((item) => (item.id === id ? { ...item, selectedValue: "" } : item));
    setData(updatedData);
  };

  const handleSubmit = (data, clearCalled = false) => {
    filterObj.current = data?.reduce((acc, item) => {
      acc[item.name] = item.selectedValue;
      if (!requiredEle.includes(item?.name)) {
        item?.requiredWith && item.selectedValue && requiredEle.push(item?.requiredWith);
        item?.required && requiredEle.push(item?.name);
      }
      return acc;
    }, {});

    requiredEle = requiredEle.filter((el) => !filterObj.current[el]);
    setRequiredEle(requiredEle);

    if (!clearCalled && requiredEle.length) {
      return false;
    }

    navigateToQuery(false);
  };

  const navigateToQuery = (isFetch = false) => {
    const { pathname, search } = location;
    let searchObj = preserveQueries.length ? Object.fromEntries(Object.entries(helper.getQueryParams(search)).filter((entry) => preserveQueries.includes(entry[0]))) : {};
    const queryString = helper.updateMultipleParameter({ ...filterObj.current, ...searchObj });
    navigate(`${pathname}?${queryString}`, { replace: true, state: { isFetch } });
    if(!requiredEle.length) {
      onSearchChange(queryString);
    }
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const clearSelectedValues = () => {
    const clearedData = data?.map((item) => {
      item.selectedValue = "";
      return item;
    });
    setData(clearedData);
    setRequiredEle([]);
    handleSubmit(clearedData, true);
  };

  const handleOpenModal = () => {
    modalRef?.current?.openModal();
  };

  const handleConfirmation = (confirmed) => {
    if (confirmed && onConfirm) {
      onConfirm(true);
    }
    navigateToQuery(true);
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
    sessionStorage.setItem("fetchWithoutFilter", true);
  };

  const updateError = (name) => {
    let updateRequiredEle = requiredEle.filter((list) => list !== name);
    setRequiredEle(updateRequiredEle);
  };

  useEffect(() => {
    if (onFilterUpdate?.current) {
      onFilterUpdate.current({ data, setData, prevChangeItem });
    }
  }, [data, onFilterUpdate]);

  const debouncedSearch = useCallback(
    helper.debouncing((value, item) => {
      handleSearchChange(value, item);
    }, 1000),
    [],
  );

  const handleSearchChange = useCallback(async (value, item) => {
    try {
      setSearchLoader(true);
      setAutoSearchData([]);
      const query = value.trim();
      if (!query && query.length < 3) return;
      const response = await item?.searchEndPoint(query);
      const { data } = response;
      setAutoSearchData(data ?? []);
    } catch (error) {
      console.error(error);
    } finally {
      setSearchLoader(false);
    }
  }, []);

  return (
    <>
      <Panel bordered className="bg-white">
        <Row className="show-grid" gutter={16} style={{ display: 'flex', flexWrap: 'wrap' }}>
          {data.map((item, index) => (
            <React.Fragment key={index}>
              {item?.type === "text" && (
                <Col xs={24} md={searchWidth || 6}>
                  <div className="filter-group">
                    <Text muted className="mb-5" maxLines={1}>
                      {item.label ? item.label : "\u00A0"}
                    </Text>
                    <InputGroup inside>
                      <InputGroup.Addon style={{ paddingRight: 10 }}>
                        <SearchIcon />
                      </InputGroup.Addon>
                      <Input
                        value={item.selectedValue || ""}
                        placeholder={item.label}
                        onChange={(value) => {
                          updateError(item?.name);
                          handleChange(value, item.id);
                        }}
                        autoComplete="off"
                      />
                    </InputGroup>
                    <ShowFormError show={requiredEle.includes(item.name)}>{`${item.label} is required`}</ShowFormError>
                  </div>
                </Col>
              )}

              {item.type === "select" && (
                <Col xs={24} md={6}>
                  <div className="filter-group">
                    <Text muted className="mb-5" maxLines={1}>
                      {item.label ? item.label : "\u00A0"}
                    </Text>
                    <SelectPicker
                      value={item.selectedValue || ""}
                      placeholder={item.label}
                      data={item.value?.map((item) => ({
                        label: item.value,
                        value: item.key,
                      }))}
                      block
                      searchable={item.searchAble}
                      cleanable={true}
                      onChange={(value) => {
                        handleChange(value, item.id);
                      }}
                      onOpen={(value) => {
                        handleChange(value, item.id);
                        updateError(item?.name);
                      }}
                    />
                    <ShowFormError show={requiredEle.includes(item.name)}>{`${item.label} is required`}</ShowFormError>
                  </div>
                </Col>
              )}

              {item.type === "date" && (
                <Col xs={24} md={6}>
                  <div className="filter-group">
                    <Text muted className="mb-5" maxLines={1}>
                      {item.label ? item.label : "\u00A0"}
                    </Text>
                    <DatePicker
                      block
                      oneTap
                      placeholder={`Select ${item.label}`}
                      value={item.selectedValue ? new Date(item.selectedValue) : null}
                      onChange={(value) => {
                        handleChange(moment(value).format("YYYY-MM-DD"), item.id);
                        updateError(item?.name);
                      }}
                      onOpen={() => {
                        updateError(item?.name);
                      }}
                      onClean={() => handleClean(item.id)}
                      cleanable={isCleanable}
                      editable={false}
                      onKeyDown={(e) => {
                        e.preventDefault();
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                      }}
                      shouldDisableDate={shouldDisableDate[item.name]}
                    />
                    <ShowFormError show={requiredEle.includes(item.name)}>{`${item.label} is required`}</ShowFormError>
                  </div>
                </Col>
              )}

              {item.type === "autoComplete" && (
                <Col xs={24} md={6}>
                  <div className="filter-group">
                    <Text muted className="mb-5" maxLines={1}>
                      {item.label ? item.label : "\u00A0"}
                    </Text>
                    <InputGroup>
                      <AutoComplete
                        value={item.selectedValue || ""}
                        placeholder={item.label}
                        searchable={item.searchAble}
                        data={autoSearchData}
                        onChange={(value) => {
                          handleChange(value, item.id);
                          debouncedSearch(value, item);
                        }}
                      />
                      <InputGroup.Addon tabIndex={-1} style={{ backgroundColor: "transparent" }}>
                        {searchLoader && <LoaderInline show={item.loading} size="xs" />}
                      </InputGroup.Addon>
                    </InputGroup>
                    <ShowFormError show={requiredEle.includes(item.name)}>{`${item.label} is required`}</ShowFormError>
                  </div>
                </Col>
              )}
            </React.Fragment>
          ))}

          <Col xs={24} md={4}>
            <div className="filter-group">
              {buttonLabel && (
                <Text muted className="mb-5">
                  &nbsp;
                </Text>
              )}
              <ButtonToolbar>
                <Button
                  block
                  color="blue"
                  appearance="primary"
                  size="md"
                  onClick={() => {
                    if (showConformation && !requiredEle.length && !data?.some((item) => `${item.selectedValue}`?.trim() !== "")) {
                      handleOpenModal();
                      return;
                    }
                    handleSubmit(data);
                  }}
                  loading={showInlineLoader}
                  disabled={disabled}
                >
                  {searchBtnLabel}
                </Button>
              </ButtonToolbar>
            </div>
          </Col>

          <Col xs={24} md={4}>
            <div className="filter-group">
              {buttonLabel && (
                <Text muted className="mb-5">
                  &nbsp;
                </Text>
              )}
              <ButtonToolbar>
                <Button
                  block
                  color="red"
                  appearance="primary"
                  size="md"
                  onClick={() => {
                    setRequiredEle([]);
                    clearSelectedValues();
                  }}
                >
                  Clear Filter
                </Button>
              </ButtonToolbar>
            </div>
          </Col>
        </Row>
      </Panel>

      {/* Loader */}
      {loading && <Loader />}
      <Suspense fallback={<Loader />}>
        <ConformationModal ref={modalRef} onConfirm={handleConfirmation} />
      </Suspense>
    </>
  );
});
